// MaturaStatsTableMeanMedianModeOpenTask.js
// Zadanie maturalne – otwarte (0–3 pkt)
// Tabela liczebności: średnia, mediana, dominanta i liczba wyników < średniej.

import React from "react";
import { InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


/* ============================================================================
 * Helpers: latex / string -> number, formatowanie LaTeX
 * (skopiowane i lekko odchudzone z MaturaBarChartStatsOpenTask)
 * ========================================================================== */

function getRaw(value) {
  if (value == null) return "";

  // event z inputa
  if (typeof value === "object" && value.target && "value" in value.target) {
    return String(value.target.value ?? "");
  }

  // najczęstsze pola z naszych inputów
  if (typeof value === "object") {
    if ("raw" in value && value.raw != null) return String(value.raw);
    if ("latex" in value && value.latex != null) return String(value.latex);
    if ("value" in value && value.value != null && typeof value.value !== "object")
      return String(value.value);
    if ("text" in value && value.text != null) return String(value.text);
    if ("expr" in value && value.expr != null) return String(value.expr);
  }

  return String(value);
}


function mixedToNumber(wholeStr, numStr, denStr) {
  const w = parseInt(wholeStr, 10);
  const n = parseInt(numStr, 10);
  const d = parseInt(denStr, 10);
  if (!d) return null;
  const sign = w < 0 ? -1 : 1;
  return w + sign * (Math.abs(n) / Math.abs(d));
}

function toNumber(value) {
  const raw0 = getRaw(value);
  if (raw0 == null) return null;
  let raw = raw0.trim();
  if (!raw) return null;

  raw = raw.replace(/\\%/g, "%");
  raw = raw.replace(/\s+/g, " ");

  const s = raw.replace(",", ".");

  // mieszany latex: k\frac{a}{b}
  let m = s.match(/^(-?\d+)\s*\\frac\{(-?\d+)\}\{(-?\d+)\}$/);
  if (m) {
    return mixedToNumber(m[1], m[2], m[3]);
  }

  // prosty \frac{a}{b}
  const fracLatex = s.match(/^\\frac\{(-?\d+)\}\{(-?\d+)\}$/);
  if (fracLatex) {
    const num = parseFloat(fracLatex[1]);
    const den = parseFloat(fracLatex[2]);
    if (den !== 0) return num / den;
  }

  // mieszany ascii: k a/b
  const mixedAscii = s.match(/^(-?\d+)\s+(-?\d+)\/(-?\d+)$/);
  if (mixedAscii) {
    return mixedToNumber(mixedAscii[1], mixedAscii[2], mixedAscii[3]);
  }

  // prosty ascii: a/b
  const fracAscii = s.match(/^(-?\d+)\/(-?\d+)$/);
  if (fracAscii) {
    const num = parseFloat(fracAscii[1]);
    const den = parseFloat(fracAscii[2]);
    if (den !== 0) return num / den;
  }

  // liczba dziesiętna / całkowita
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    return parseFloat(s);
  }

  return null;
}

function numbersEqual(a, b, eps = 1e-6) {
  if (a == null || b == null) return false;
  return Math.abs(a - b) < eps;
}

function gcdInt(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

// num, den – całkowite
function mixedFractionLatex(num, den) {
  const whole = Math.trunc(num / den);
  const rem = Math.abs(num % den);
  const absDen = Math.abs(den);

  if (rem === 0) {
    return String(whole);
  }
  if (whole === 0) {
    return `\\frac{${rem}}{${absDen}}`;
  }
  return `${whole}\\frac{${rem}}{${absDen}}`;
}

/**
 * Formatowanie wpisu użytkownika do ładnego LaTeX:
 * - "1/2"      -> "\frac{1}{2}"
 * - "2 3/5"    -> "2\frac{3}{5}"
 * - "1,5"      -> "1.5"
 * Jeśli user wpisał już LaTeX (np. "\frac{1}{2}"), zostawiamy.
 */
function formatUserLatex(value) {
  let raw = getRaw(value);
  if (raw == null) raw = "";
  raw = raw.trim();
  if (!raw) {
    return "\\text{–}";
  }

  let s = raw.replace(/\\%/g, "%").trim();
  let latex = s;

  if (!s.startsWith("\\")) {
    const mixedAscii = s.match(/^(-?\d+)\s+(-?\d+)\/(-?\d+)$/);
    const fracAscii = s.match(/^(-?\d+)\/(-?\d+)$/);

    if (mixedAscii) {
      const whole = mixedAscii[1];
      const num = Math.abs(parseInt(mixedAscii[2], 10));
      const den = Math.abs(parseInt(mixedAscii[3], 10));
      latex = `${whole}\\frac{${num}}{${den}}`;
    } else if (fracAscii) {
      const num = parseInt(fracAscii[1], 10);
      const den = parseInt(fracAscii[2], 10);
      latex = `\\frac{${num}}{${den}}`;
    } else {
      latex = s.replace(",", ".");
    }
  }

  return latex;
}

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

/* ============================================================================
 * Scenariusze
 * ========================================================================== */

const SCENARIOS = [
  {
    id: "math_test",
    intro:
      "W tabeli zestawiono liczbę punktów uzyskanych przez uczniów pewnej klasy za rozwiązanie jednego z zadań ze sprawdzianu z matematyki.",
    xHeader: "Liczba punktów",
    yHeader: "Liczba uczniów, którzy otrzymali daną liczbę punktów",
    yHeaderLines: [
      "Liczba uczniów,",
      "którzy otrzymali",
      "daną liczbę punktów",
    ],
    entityPlural: "uczniów",
    whatPlural: "punktów",
    // kategorie w zakresie 0–5, długość 4–6
    minValRange: [0, 1],
    maxValRange: [4, 5],
  },
  {
    id: "mushrooms_baskets",
    intro:
      "W tabeli zestawiono liczbę koszyków grzybów zebranych przez członków koła emerytów podczas wspólnej wyprawy do lasu.",
    xHeader: "Liczba koszyków grzybów",
    yHeader: "Liczba osób, które zebrały daną liczbę koszyków",
    yHeaderLines: [
      "Liczba osób,",
      "które zebrały",
      "daną liczbę koszyków",
    ],
    entityPlural: "osób",
    whatPlural: "koszyków",
    // chcemy wartości od 2 do 6 – pięć kolejnych liczb
    minValRange: [2, 2],
    maxValRange: [6, 6],
  },
  {
    id: "books_read",
    intro:
      "W tabeli zestawiono liczbę książek przeczytanych w ciągu miesiąca przez uczniów biorących udział w szkolnej akcji czytelniczej.",
    xHeader: "Liczba przeczytanych książek",
    yHeader: "Liczba uczniów, którzy przeczytali daną liczbę książek",
    yHeaderLines: [
      "Liczba uczniów,",
      "którzy przeczytali",
      "daną liczbę książek",
    ],
    entityPlural: "uczniów",
    whatPlural: "książek",
    // wartości 1..8, długość 4–6
    minValRange: [1, 3],
    maxValRange: [6, 8],
  },
];


function getScenarioById(id) {
  return SCENARIOS.find((s) => s.id === id) || SCENARIOS[0];
}

/* ============================================================================
 * Generator: kategorie + liczebności
 * ========================================================================== */

// losujemy ciągły zakres długości 4–6 w zadanym przedziale min/max
function generateCategoriesForScenario(scenario) {
  const [minA, minB] = scenario.minValRange;
  const [maxA, maxB] = scenario.maxValRange;

  const globalMin = minA + Math.floor(Math.random() * (minB - minA + 1));
  const globalMax = maxA + Math.floor(Math.random() * (maxB - maxA + 1));

  const maxLen = Math.min(6, globalMax - globalMin + 1);
  const minLen = Math.min(4, maxLen); // gdyby zakres był wąski
  const lenOptions = [];
  for (let L = minLen; L <= maxLen; L += 1) lenOptions.push(L);

  const len = pickRandom(lenOptions); // 4–6 (albo mniej, jeśli zakres krótszy)

  const maxStart = globalMax - len + 1;
  const start = globalMin + Math.floor(Math.random() * (maxStart - globalMin + 1));

  const cats = [];
  for (let v = start; v < start + len; v += 1) {
    cats.push(v);
  }
  return cats;
}

function hasUniqueMode(counts) {
  const maxCount = Math.max(...counts);
  let howManyMax = 0;
  for (let i = 0; i < counts.length; i += 1) {
    if (counts[i] === maxCount) howManyMax += 1;
  }
  return howManyMax === 1;
}

function generateCountsForCategories(len, totalEntities) {
  // najpierw po 1, żeby nikt nie miał 0
  const counts = new Array(len).fill(1);
  let remaining = totalEntities - len;

  while (remaining > 0) {
    const idx = Math.floor(Math.random() * len);
    counts[idx] += 1;
    remaining -= 1;
  }

  return counts;
}

/* ============================================================================
 * Generator zadania
 * ========================================================================== */

function generateJune2025Original() {
  return {
    type: "MaturaStatsTableMeanMedianModeOpenTask",
    data: {
      scenarioId: "math_test",
      categories: [0, 1, 2, 3, 4, 5],
      counts: [4, 2, 5, 5, 11, 5],
      total: 32,
      modeValue: 4,
      mean: 3,
      meanNum: 3,
      meanDen: 1,
      median: 3.5,
      medianNum: 7,
      medianDen: 2,
      belowMeanCount: 11,
      weightedSum: 96,
      maxPoints: 3,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 30,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return generateJune2025Original();
  }

  const scenario = pickRandom(SCENARIOS);

  // kategorie – ciąg arytmetyczny o różnicy 1, 4–6 wartości
  const categories = generateCategoriesForScenario(scenario);

  // losujemy liczebności aż dostaniemy jednoznaczną dominantę
  const len = categories.length;
  const totalEntities = 24 + Math.floor(Math.random() * 13); // 24..36

  let counts = null;
  for (let attempt = 0; attempt < 30; attempt += 1) {
    const candidate = generateCountsForCategories(len, totalEntities);
    if (hasUniqueMode(candidate)) {
      counts = candidate;
      break;
    }
  }
  if (!counts) counts = generateCountsForCategories(len, totalEntities);

  const total = counts.reduce((a, b) => a + b, 0);

  // dominanta
  const maxCount = Math.max(...counts);
  const modeIndex = counts.indexOf(maxCount);
  const modeValue = categories[modeIndex];

  // średnia
  const weightedSum = categories.reduce(
    (acc, cat, idx) => acc + cat * counts[idx],
    0
  );
  const meanNumRaw = weightedSum;
  const meanDenRaw = total;
  const gMean = gcdInt(meanNumRaw, meanDenRaw);
  const meanNum = meanNumRaw / gMean;
  const meanDen = meanDenRaw / gMean;
  const mean = meanNumRaw / meanDenRaw;

  // mediana – odtwarzamy listę wyników
  const allValues = [];
  categories.forEach((cat, i) => {
    for (let k = 0; k < counts[i]; k += 1) {
      allValues.push(cat);
    }
  });
  allValues.sort((a, b) => a - b);

  let medianNum;
  let medianDen;
  const n = allValues.length;
  if (n % 2 === 1) {
    // nieparzysta
    medianNum = allValues[(n - 1) / 2];
    medianDen = 1;
  } else {
    const mid1 = allValues[n / 2 - 1];
    const mid2 = allValues[n / 2];
    medianNum = mid1 + mid2;
    medianDen = 2;
    const gMed = gcdInt(medianNum, medianDen);
    medianNum /= gMed;
    medianDen /= gMed;
  }
  const median = medianNum / medianDen;

  // ile uczniów ma wynik < średniej
  let belowMeanCount = 0;
  categories.forEach((cat, i) => {
    if (cat < mean) {
      belowMeanCount += counts[i];
    }
  });

  return {
    type: "MaturaStatsTableMeanMedianModeOpenTask",
    data: {
      scenarioId: scenario.id,
      categories,
      counts,
      total,
      modeValue,
      mean,
      meanNum,
      meanDen,
      median,
      medianNum,
      medianDen,
      belowMeanCount,
      weightedSum,
      maxPoints: 3,
    },
  };
}

/* ============================================================================
 * Treść zadania
 * ========================================================================== */

export function renderTask({ data }) {
  const { scenarioId, categories, counts, total } = data;
  const scenario = getScenarioById(scenarioId);

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – otwarte (0–3 pkt).</strong>
      </p>

      <p>{scenario.intro}</p>

      <p>Otrzymane dane zestawiono w tabeli.</p>

      <div style={{ maxWidth: "100%", overflowX: "auto" }}>
      <table
        style={{
          borderCollapse: "collapse",
          marginTop: "8px",
          marginBottom: "8px",
          minWidth: "280px",
          marginLeft: "auto",    // <–– to
          marginRight: "auto",   // <–– i to
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                border: "1px solid #ccc",
                padding: "8px 12px",        // większy margines góra/dół
                textAlign: "center",
              }}
            >
              {scenario.xHeader}
            </th>
            {categories.map((cat) => (
              <th
                key={`h-${cat}`}
                style={{
                  border: "1px solid #ccc",
                  padding: "8px 12px",      // większy margines
                  textAlign: "center",
                }}
              >
                <InlineMath math={String(cat)} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              style={{
                border: "1px solid #ccc",
                padding: "8px 6px",        // trochę wyższa komórka
                textAlign: "center",
                whiteSpace: "normal",
              }}
            >
              {scenario.yHeaderLines
                ? scenario.yHeaderLines.map((line, idx) => (
                    <div key={idx}>{line}</div>
                  ))
                : scenario.yHeader}
            </td>
            {counts.map((cnt, idx) => (
              <td
                key={`c-${idx}`}
                style={{
                  border: "1px solid #ccc",
                  padding: "8px 12px",      // większy margines dla liczb
                  textAlign: "center",
                }}
              >
                <InlineMath math={String(cnt)} />
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      </div>


      <p>
        Łącznie w tabeli uwzględniono{" "}
        <InlineMath math={String(total)} /> {scenario.entityPlural}.
      </p>

      <p>
        Uzupełnij zdania. Wpisz odpowiednie liczby w wykropkowanych miejscach,
        aby zdania były prawdziwe.
      </p>

      <ol className="list-decimal ml-6 space-y-1">
        <li>
          Wynik niższy od średniej arytmetycznej liczby {scenario.whatPlural}{" "}
          uzyskało dokładnie <InlineMath math={"\\_\\_\\_\\_"} />{" "}
          {scenario.entityPlural}.
        </li>
        <li>
          Mediana liczby {scenario.whatPlural} jest równa{" "}
          <InlineMath math={"\\_\\_\\_\\_"} /> .
        </li>
        <li>
          Dominanta liczby {scenario.whatPlural} jest równa{" "}
          <InlineMath math={"\\_\\_\\_\\_"} /> .
        </li>
      </ol>
    </div>
  );
}

/* ============================================================================
 * Input – trzy pola FractionInput
 * ========================================================================== */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  const setField =
    (key) =>
    (val) =>
      onChange({ ...(ua || {}), [key]: val });

  return (
    <div style={{ display: "grid", rowGap: 16, maxWidth: 420 }}>
      <OpenTaskScoringHint variant="independent" />
      <div style={{ display: "grid", rowGap: 4 }}>
        <div>
          <strong>1.</strong> Liczba osób z wynikiem mniejszym niż średnia:
        </div>
        <FractionInput
          value={ua.d1}
          onChange={setField("d1")}
          disabled={disabled}
          placeholder="np. 11"
          label=""
        />
      </div>

      <div style={{ display: "grid", rowGap: 4 }}>
        <div>
          <strong>2.</strong> Mediana:
        </div>
        <FractionInput
          value={ua.d2}
          onChange={setField("d2")}
          disabled={disabled}
          placeholder="np. 3 lub 3 1/2"
          label=""
        />
      </div>

      <div style={{ display: "grid", rowGap: 4 }}>
        <div>
          <strong>3.</strong> Dominanta:
        </div>
        <FractionInput
          value={ua.d3}
          onChange={setField("d3")}
          disabled={disabled}
          placeholder="np. 4"
          label=""
        />
      </div>
    </div>
  );
}

/* ============================================================================
 * Sprawdzanie (0–3 pkt)
 * ========================================================================== */

export function checkAnswer({ data }, userAnswer) {
  const ua = userAnswer || {};
  const { belowMeanCount, median, modeValue } = data;

  const d1Val = toNumber(ua.d1);
  const d2Val = toNumber(ua.d2);
  const d3Val = toNumber(ua.d3);

  const d1Ok = numbersEqual(d1Val, belowMeanCount);
  const d2Ok = numbersEqual(d2Val, median);
  const d3Ok = numbersEqual(d3Val, modeValue);

  const correctCount = [d1Ok, d2Ok, d3Ok].filter(Boolean).length;

  let score = 0;
  if (correctCount === 3) score = 3;
  else if (correctCount === 2) score = 2;
  else if (correctCount === 1) score = 1;

  return {
    isCorrect: score === 3,
    correct: score === 3,
    score,
    maxScore: 3,
    parts: {
      "1) poniżej średniej": d1Ok,
      "2) mediana": d2Ok,
      "3) dominanta": d3Ok,
    },
  };
}

/* ============================================================================
 * Poprawna odpowiedź
 * ========================================================================== */

export function renderAnswer({ data }) {
  const { belowMeanCount, medianNum, medianDen, modeValue } = data;

  const medianLatex = mixedFractionLatex(medianNum, medianDen);

  return (
    <div style={{ marginTop: 8, display: "grid", rowGap: 4 }}>
      <div>
        <strong>1.</strong> <InlineMath math={String(belowMeanCount)} />
      </div>
      <div>
        <strong>2.</strong> <InlineMath math={medianLatex} />
      </div>
      <div>
        <strong>3.</strong> <InlineMath math={String(modeValue)} />
      </div>
    </div>
  );
}

/* ============================================================================
 * Podgląd odpowiedzi użytkownika
 * ========================================================================== */

export function renderUserAnswer(userAnswer) {
  const isCompletelyEmpty = () => {
    if (!userAnswer) return true;
    const keys = ["d1", "d2", "d3"];
    return keys.every((key) => {
      const v = userAnswer[key];
      if (v == null) return true;
      const raw =
        typeof v === "object" && v.raw != null ? String(v.raw) : String(v);
      return raw.trim() === "";
    });
  };

  if (isCompletelyEmpty()) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { d1, d2, d3 } = userAnswer;

  const latexOrDash = (field) => formatUserLatex(field);

  return (
    <div style={{ marginTop: 8, display: "grid", rowGap: 4 }}>
      <div>
        <strong>1.</strong> <InlineMath math={latexOrDash(d1)} />
      </div>
      <div>
        <strong>2.</strong> <InlineMath math={latexOrDash(d2)} />
      </div>
      <div>
        <strong>3.</strong> <InlineMath math={latexOrDash(d3)} />
      </div>
    </div>
  );
}

/* ============================================================================
 * Wyjaśnienie krok po kroku
 * ========================================================================== */

export function renderExplanation({ data }) {
  const {
    scenarioId,
    categories,
    counts,
    total,
    modeValue,
    mean,
    meanNum,
    meanDen,
    median,
    medianNum,
    medianDen,
    belowMeanCount,
    weightedSum,
  } = data;

  const scenario = getScenarioById(scenarioId);

  const meanLatex = mixedFractionLatex(meanNum, meanDen);
  const medianLatex = mixedFractionLatex(medianNum, medianDen);
  const meanRounded2 = (Math.round(mean * 100) / 100).toFixed(2).replace(".", "{,}");
  const medianRounded2 = (Math.round(median * 100) / 100).toFixed(2).replace(".", "{,}");

  // Mediana – pozycje
  const n = total;
  const isOdd = n % 2 === 1;
  const pos1 = isOdd ? (n + 1) / 2 : n / 2;
  const pos2 = isOdd ? null : n / 2 + 1;

  // Wyznaczamy, na jakie wartości wpadają pozycje mediany (pos1/pos2)
  let cum = 0;
  let val1 = null;
  let val2 = null;
  let prev = 0;
  for (let i = 0; i < categories.length; i++) {
    prev = cum;
    cum += counts[i];
    if (val1 == null && pos1 > prev && pos1 <= cum) val1 = categories[i];
    if (val2 == null && pos2 > prev && pos2 <= cum) val2 = categories[i];
  }

  return (
    <div style={{ lineHeight: 1.7 }}>
      {/* KROK 1 */}
      <p>
        <strong>Krok 1.</strong> Z tabeli odczytujemy, ilu {scenario.entityPlural}{" "}
        przypada na każdą liczbę {scenario.whatPlural}.
      </p>

      <ul className="list-disc ml-6">
        {categories.map((cat, i) => (
          <li key={cat}>
            liczba {scenario.whatPlural}: <InlineMath math={String(cat)} /> – liczba{" "}
            {scenario.entityPlural}: <InlineMath math={String(counts[i])} />
          </li>
        ))}
      </ul>

      <p>
        Suma wszystkich {scenario.entityPlural} wynosi{" "}
        <InlineMath math={String(total)} />.
      </p>

      {/* KROK 2 – średnia arytmetyczna */}
      <p>
        <strong>Krok 2.</strong> Obliczamy średnią arytmetyczną liczby{" "}
        {scenario.whatPlural}. Korzystamy ze wzoru{" "}
        <InlineMath math={"\\bar{x} = \\dfrac{\\sum x_i n_i}{\\sum n_i}"} />.
      </p>

      <p>
        Mamy{" "}
        <InlineMath
          math={`\\sum x_i n_i = ${weightedSum},\\quad \\sum n_i = ${total}.`}
        />
      </p>

      <p>
        Stąd{" "}
        <InlineMath
          math={`\\bar{x} = \\dfrac{${weightedSum}}{${total}} = ${meanLatex} \\approx ${meanRounded2}.`}
        />
      </p>

      <p>
        Uzyskana średnia pozwala nam teraz ustalić, ilu {scenario.entityPlural}{" "}
        uzyskało wynik mniejszy od tej wartości.
      </p>

      {/* KROK 3 – ile wyników < średniej */}
      <p>
        <strong>Krok 3.</strong> Wynik niższy od średniej uzyskały te osoby,
        których liczba {scenario.whatPlural} jest mniejsza niż{" "}
        <InlineMath math={meanLatex} />.
      </p>

      <p>
        Z tabeli zliczamy wszystkich {scenario.entityPlural}, dla których liczba{" "}
        {scenario.whatPlural} jest mniejsza niż <InlineMath math={meanLatex} />.
        Daje to <InlineMath math={String(belowMeanCount)} /> {scenario.entityPlural}.
      </p>

      {/* KROK 4 – mediana (bez wypisywania całej listy) */}
      <p>
        <strong>Krok 4.</strong> Medianę wyznaczamy przez wskazanie pozycji
        środkowej w uporządkowanym ciągu wyników.
      </p>

      <p>
        Mamy łącznie <InlineMath math={String(n)} /> {scenario.entityPlural}.{" "}
        {isOdd ? (
          <>
            Ponieważ to liczba nieparzysta, mediana to element na pozycji{" "}
            <InlineMath math={String(pos1)} />.
          </>
        ) : (
          <>
            Ponieważ to liczba parzysta, bierzemy elementy na pozycjach{" "}
            <InlineMath math={String(pos1)} /> oraz{" "}
            <InlineMath math={String(pos2)} />, a potem liczymy ich średnią.
          </>
        )}
      </p>

      <p>Wyznaczamy, w jakich przedziałach pozycji znajdują się kolejne wartości:</p>

      <ul className="list-disc ml-6">
        {categories.map((cat, i) => {
          const from = i === 0 ? 1 : counts.slice(0, i).reduce((a, b) => a + b, 0) + 1;
          const to = counts.slice(0, i + 1).reduce((a, b) => a + b, 0);
          return (
            <li key={cat}>
              <InlineMath math={String(cat)} /> odpowiada pozycjom od{" "}
              <InlineMath math={String(from)} /> do <InlineMath math={String(to)} />
            </li>
          );
        })}
      </ul>

      <p>
        Pozycja <InlineMath math={String(pos1)} /> wypada w wartości{" "}
        <InlineMath math={String(val1)} />
        {pos2 != null ? (
          <>
            , a pozycja <InlineMath math={String(pos2)} /> w wartości{" "}
            <InlineMath math={String(val2)} />
          </>
        ) : null}
        . Zatem{" "}
        <strong>mediana</strong>{" "}
        {pos2 == null ? (
          <>
            wynosi <InlineMath math={String(val1)} />.
          </>
        ) : (
          <>
            to średnia tych dwóch wartości, czyli{" "}
            <InlineMath math={`${medianLatex} \\approx ${medianRounded2}`} />.
          </>
        )}
      </p>

      {/* KROK 5 – dominanta */}
      <p>
        <strong>Krok 5.</strong> Dominanta to wartość, która występuje najczęściej.
        Widzimy, że największa liczebność występuje przy liczbie{" "}
        <InlineMath math={String(modeValue)} />, więc{" "}
        <strong>dominanta</strong> wynosi{" "}
        <InlineMath math={String(modeValue)} />.
      </p>
    </div>
  );
}
